// Selection of cells in the grid. Pure-data set wrapper with a version
// counter so derived views (the selection outline mesh, the move
// handler) can detect changes by integer comparison.
//
// Selection holds only cell coordinates — not palette values. The
// authoritative palette is still on the grid; the selection just marks
// which cells the next tool action operates on. (Move handler reads the
// palette per cell at apply time.)
//
// Cells are stored as a single packed Number per cell (the same
// bias/range scheme as VoxelGrid's chunkKey) rather than "x,y,z"
// strings: a big marquee holds thousands of cells, and string keys cost
// an allocation per cell on every replace() plus a split+3×Number
// reparse per cell on every entries() walk.

type CellCoord = { ix: number; iy: number; iz: number };

// 16-bit per-axis range (-32768..32767) leaves the packed key well
// under Number.MAX_SAFE_INTEGER (2^53); the work volume is far smaller.
const KEY_BIAS = 32768;
const KEY_RANGE = 65536; // 2^16
const KEY_RANGE_SQ = KEY_RANGE * KEY_RANGE; // 2^32

export class Selection {
  private cells = new Set<number>();
  version = 0;

  size(): number {
    return this.cells.size;
  }

  has(ix: number, iy: number, iz: number): boolean {
    return this.cells.has(cellKey(ix, iy, iz));
  }

  // Replace the entire selection with the given iterable. Bumps version
  // exactly once (regardless of how many cells changed) so the mesher
  // does one rebuild per replacement, not one per cell.
  replace(cells: Iterable<CellCoord>): void {
    this.cells.clear();
    for (const c of cells) {
      this.cells.add(cellKey(c.ix, c.iy, c.iz));
    }
    this.version++;
  }

  *entries(): IterableIterator<CellCoord> {
    for (const k of this.cells) {
      yield {
        ix: Math.floor(k / KEY_RANGE_SQ) - KEY_BIAS,
        iy: (Math.floor(k / KEY_RANGE) % KEY_RANGE) - KEY_BIAS,
        iz: (k % KEY_RANGE) - KEY_BIAS,
      };
    }
  }
}

function cellKey(ix: number, iy: number, iz: number): number {
  return (
    (ix + KEY_BIAS) * KEY_RANGE_SQ + (iy + KEY_BIAS) * KEY_RANGE + (iz + KEY_BIAS)
  );
}
